import { logSwallowed } from '@/lib/errors';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
// Шрифт Inter — САМОХОСТИНГ (без внешнего CDN Google Fonts): woff2 собираются
// в dist/assets, раздаёт наш сервер, кэширует Service Worker — работает штатно
// и офлайн, никаких 404 с fonts.gstatic.com.
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
// Шрифты САЙТОВ (концепт «Квантум») — тоже self-host (без внешних CDN):
// Manrope — основной текст сайта, Space Grotesk — заголовки (--site-font*).
import '@fontsource/manrope/400.css';
import '@fontsource/manrope/500.css';
import '@fontsource/manrope/600.css';
import '@fontsource/manrope/700.css';
import '@fontsource/manrope/800.css';
import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import './index.css';
import { syncEngine } from '@/lib/syncEngine';
import { baseStoreGet } from '@/lib/storage/baseStore';
import { initEventLogFlush } from '@/lib/eventLog';
import { usePwaInstallStore } from '@/lib/pwaInstall';
import { isWailsMode, refreshWailsConfig } from '@/lib/wailsBridge';
import { initContextChat } from '@/lib/contextChat';
import { eventBusHub } from '@/lib/eventBusHub';
import { useAuthStore } from '@/store/authStore';
import { useCenterStore } from '@/store/centerStore';
import { hydrateLauncherStorage, flushLauncherStorage } from '@/lib/launcherStorage';
import { initChatQueue } from '@/lib/chatQueue';
import { useConnectionStore } from '@/lib/connection';
import { initUpdateNotify } from '@/lib/updateNotify';
import { registerBuiltinWidgets } from '@/lib/widgets/builtin';
import { registerChatApp } from '@/components/apps/chatRenderer';
import { registerExplorerApp } from '@/components/apps/explorerRenderer';
import { registerMailApp } from '@/components/apps/mailRenderer';
import { registerRemoteApp } from '@/components/apps/remoteRenderer';
import { registerVpnApp } from '@/components/apps/vpnRenderer';
import { registerVoiceRoomsApp } from '@/components/apps/voiceRoomsRenderer';
import { registerPatientCabinetApp } from '@/components/apps/patientCabinetRenderer';
import { registerMedicAppointmentsApp } from '@/components/apps/medicAppointmentsRenderer';
import { registerEcgApp } from '@/components/apps/ecgRenderer';
import { registerSupportInboxApp } from '@/components/apps/supportRenderer';
import { registerDevopsApp } from '@/components/apps/devopsRenderer';
import { registerCabinetApp } from '@/components/apps/cabinetRenderer';
import { registerPsyApp } from '@/components/apps/psyRenderer';
import { registerSedApp } from '@/components/sed/sedRenderer';
// «Заявки на услуги» — клиентская витрина маркетплейса (hydra.market, mkt_requests)
import { registerMarketClientApp } from '@/components/apps/marketClientRenderer';
import { registerBuiltinAppEmbeds } from '@/lib/appEmbedsBuiltin';
import { createLogger } from '@/lib/logger';
import { initOfflineFlag, isForcedOffline, subscribeOfflineFlag } from '@/lib/offlineFlag';
import { initMainThreadWatchdog, reportPreviousWatchdogReload } from '@/lib/mainThreadWatchdog';
import { I18nProvider } from '@/lib/i18n/I18nProvider';

// «Автономный режим» (офлайн-фёрст): флаг читаем ДО остальных инициаторов —
// от него зависят связь (connection), шина (ниже) и слои данных.
initOfflineFlag();

// ОКНО УВЕДОМЛЕНИЙ (desktop): маршрут /notify грузит Go-часть (notify_window.go)
// в ОТДЕЛЬНОМ окне WebView2 — приложение здесь НЕ поднимается (см. ниже).
const isNotifyWindow = window.location.pathname === '/notify';

// DESKTOP: фактический loopback-порт ЭТОГО экземпляра известен только после
// чтения конфига Go (порт выдаёт ОС при старте процесса) — читаем ДО первых
// инициаторов SDK (ниже). web/PWA — мгновенный no-Wails конфиг.
const wailsConfigReady = refreshWailsConfig();

// ============================================================================
// PWA-WATCHDOG (самовосстановление): главный поток мог зависнуть («белое поле»,
// вечная загрузка) — пользователь снимал приложение диспетчером Chrome.
// Здесь: бит живости + независимая проверка → единожды за сессию метка
// `watchdog reload` в кольцевой буфер и перезагрузка. На следующем старте
// след уходит в Журнал регистрации (ui/warning). Desktop (Wails) — без
// перезагрузки (reload ломает мост), только запись в буфер.
// ============================================================================
if (!isNotifyWindow) {
  initMainThreadWatchdog({ canReload: () => !isWailsMode() });
  void reportPreviousWatchdogReload();
}

// Рабочие столы + Страницы: стандартные виджеты ядра (реестр — SSoT клиента)
registerBuiltinWidgets();
// ПРИЛОЖЕНИЯ (kind='app') — реестр рендереров (SSoT): чат и будущие.
registerChatApp();
registerExplorerApp();
// «Почта: Входящие» — ящики, папки, поиск (существующие RPC почтового ядра)
registerMailApp();
registerRemoteApp();
registerVpnApp();
registerVoiceRoomsApp();
// «Мой кабинет» (кабинет пациента psy): точка входа — док/«Пуск» для роли «Пациент»
registerPatientCabinetApp();
// «Запись на приём»: кабинет записи пациента/врача (medic_encounters/psy_bookings)
registerMedicAppointmentsApp();
// «ЭКГ-разбор»: разбор снимка сервисом-анализатором (роли «Врач»/«Студент»/«Преподаватель»)
registerEcgApp();
// «Поддержка» (§3.4): инбокс обращений — специалистам, «Мои обращения» — всем
registerSupportInboxApp();
// «Разработка» (hydra.devops): панель узлов/задач — роли «Разработка»/админ
registerDevopsApp();
// «Кабинет»: личная точка входа с реестром вкладок (первая — «Поддержка»)
registerCabinetApp();
// «Психолог»: рабочее место специалиста (поставка hydra.medic.psy)
registerPsyApp();
// «Документооборот»: рабочее место СЭД (поставка hydra.sed)
registerSedApp();
// «Заявки на услуги»: клиентская витрина маркетплейса (поставка hydra.market).
// В доке показывается только в базах, где есть mkt_requests + mkt_categories
// (useVisibleApps → marketPresence); прямой вход работает всегда.
registerMarketClientApp();
// EMBED-поставщики (встроенные; приложения регистрируют свои registerAppEmbed)
registerBuiltinAppEmbeds();

// Журнал регистрации (как в 1С): флаш оффлайн-очереди событий при сети/фокусе
initEventLogFlush();

// Бейджи обсуждений (контекстные чаты): подписки на события (SSoT — сервер)
initContextChat();

// Новая версия фронта: сервер публикует retained platform/frontend_version при
// старте; каждый подключившийся клиент сравнивает со своим entry и, если код
// на сервере новее, получает уведомление в колокольчик + тост «Перезагрузить»
// (единый механизм, см. swUpdate.notifyAppUpdated). Подписка на hub — активна
// всегда, стрим стартует после входа (setEnabled выше).
void import('@/lib/swUpdate').then((m) => m.subscribeServerFrontendVersion());

// EventBus-стрим — ТОЛЬКО после входа (до входа сервер 401/400) И не в
// «Автономном режиме» (связь сознательно выключена — стрим не запускаем):
// включаем при появлении пользователя/выключении автономного режима, выключаем
// при выходе/включении автономного режима.
// Первый запуск — после чтения конфига desktop: SDK не создаётся без
// фактического loopback-адреса (getLoopbackUrl бросает явную ошибку).
const syncBusEnabled = () => {
  eventBusHub.setEnabled(useAuthStore.getState().isAuthenticated && !isForcedOffline());
};
useAuthStore.subscribe((s, prev) => {
  if (s.isAuthenticated === prev.isAuthenticated) return;
  syncBusEnabled();
  // Вход после перезапуска: durable-очередь ЭКГ (снимки без разбора и
  // несохранённые разборы) отправляется сразу, не дожидаясь фокуса окна.
  if (s.isAuthenticated) {
    void import('@/lib/ecgOfflineQueue').then((m) => m.flushEcgStudyQueue().catch((e) => logSwallowed(e, 'main.tsx:118')));
  }
});
let prevForcedOffline = isForcedOffline();
subscribeOfflineFlag(() => {
  const nowForced = isForcedOffline();
  syncBusEnabled();
  // Выключили «Автономный режим» — поднимаем связь немедленно (стрим отдаст
  // online → синк очередей/подгрузки пойдут по штатным событийным путям).
  if (prevForcedOffline && !nowForced) eventBusHub.poke();
  prevForcedOffline = nowForced;
});
void wailsConfigReady.then(() => {
  syncBusEnabled();
});

// Перманентные ошибки стрима (404/403/401…) ставят паузу (stall, 30с).
// Фокус окна/сеть вернулись — перезапускаем стрим немедленно (poke).
let busPoked = false;
const pokeBus = () => {
  if (isForcedOffline()) return; // автономный режим — связь не поднимаем
  if (busPoked) return;
  busPoked = true;
  setTimeout(() => {
    busPoked = false;
    eventBusHub.poke();
  }, 0);
};
window.addEventListener('focus', pokeBus);
window.addEventListener('online', pokeBus);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') pokeBus();
});

// Очередь отправки сообщений чата (offline-first): флаш при восстановлении связи
initChatQueue();

// ВОССТАНОВЛЕНИЕ СВЯЗИ (подтверждённый online — живой стрим EventBus):
// догоняем синхронизацию очереди и отложенные загрузки. Событие, не таймер:
// пока связи нет, syncAll не запускается (режим офлайн), при восстановлении
// очередь уходит на сервер (и статус в StatusBar снова «онлайн»).
useConnectionStore.subscribe((s, prev) => {
  if (s.status === 'online' && prev.status !== 'online') {
    baseStoreGet().then((base) => {
      if (base?.id) syncEngine.scheduleSync(base.id);
    });
    void import('@/lib/fileSync').then((m) => m.syncFiles().catch((e) => logSwallowed(e, 'main.tsx:119')));
    void import('@/lib/chunkedUpload').then((m) => m.flushUploadQueue().catch((e) => logSwallowed(e, 'main.tsx:120')));
    // Офлайн-очередь разборов ЭКГ (снимок + результат): отправка при связи
    void import('@/lib/ecgOfflineQueue').then((m) => m.flushEcgStudyQueue().catch((e) => logSwallowed(e, 'main.tsx:121')));
    // Настройки пользователя (черновики чатов, состояния списков): отложенные
    // офлайн записи/удаления уходят на сервер сразу при восстановлении связи.
    void import('@/lib/settingsStore').then((m) => m.flushUserSettings());
  }
});

// СМС-шлюз (устройство-шлюз): доступно только в приложении на Android.
// При включённом флаге «Это устройство — СМС-шлюз» поднимает слушателя шины
// sms.outbox.{deviceId} и фоновую службу; на веб/PWA — ничего (секция скрыта).
void import('@/lib/sms/smsGateway').then((m) => m.initSmsGateway().catch((e) => logSwallowed(e, 'main.tsx:127')));

// Офлайн-очередь загрузки ФАЙЛОВ (offline-first): чанковая докачка при старте
void import('@/lib/chunkedUpload').then((m) => m.flushUploadQueue().catch((e) => logSwallowed(e, 'main.tsx:130')));
void import('@/lib/ecgOfflineQueue').then((m) => m.flushEcgStudyQueue().catch((e) => logSwallowed(e, 'main.tsx:131')));

// PWA-установка (beforeinstallprompt/appinstalled — глобальные слушатели)
usePwaInstallStore.getState().init();

// Единый менеджер подключения (SSoT: статус сервера/сети, событийная модель)
import('./lib/connection').then((m) => m.initConnectionManager());

// ============================================================================
// ГЛОБАЛЬНЫЙ перехват ошибок (SRP): неперехваченные ошибки/промисы пишутся
// в ЖУРНАЛ РЕГИСТРАЦИИ (категория 'ui', SSoT — сервер; офлайн — очередь).
// Пользователю их показывает AppErrorBoundary (осмысленное сообщение,
// «обратитесь в поддержку») — ошибки не глотаются.
// ============================================================================
// БУФЕР СТАРТОВЫХ ОШИБОК (диагностика «белого экрана»): на лаунчере журнала
// регистрации нет (без базы событие не пишется) — пишем в кольцо и буфер.
const bootLog = createLogger('boot');
(window as any).__bootErrors = [] as { t: string; m: string; s: string }[];
window.addEventListener('error', (e) => {
  (window as any).__bootErrors.push({ t: 'err', m: String(e.message || ''), s: String((e.error?.stack || '').slice(0, 800)) });
  bootLog.error(`window error: ${e.message || ''}`, { details: (e.error?.stack || '').slice(0, 2000) });
  import('@/lib/eventLog').then(({ eventLog }) => {
    eventLog.error('ui', 'Ошибка: ' + (e.message || 'неизвестная'), {
      details: (e.error?.stack || '').slice(0, 4000),
    }).catch((e) => { logSwallowed(e, 'main.tsx:154'); return undefined; });
  });
});
window.addEventListener('unhandledrejection', (e) => {
  const reason = e.reason;
  const msg = reason instanceof Error ? reason.message : String(reason);
  (window as any).__bootErrors.push({ t: 'rej', m: String(msg), s: String((reason?.stack || '').slice(0, 800)) });
  bootLog.error(`unhandledrejection: ${msg}`, { details: (reason?.stack || '').slice(0, 2000) });
  import('@/lib/eventLog').then(({ eventLog }) => {
    eventLog.error('ui', 'Необработанный промис: ' + msg, {
      details: (reason?.stack || '').slice(0, 4000),
    }).catch((e) => { logSwallowed(e, 'main.tsx:165'); return undefined; });
  });
});

// DESKTOP (Wails): сервис-воркер не нужен и может ОТДАВАТЬ СТАРЫЙ фронт после
// пересборки (в накопленных профилях окон). Снимаем регистрацию и чистим кэши,
// чтобы десктоп ВСЕГДА грузил вшитый (актуальный) JS.
if (isWailsMode() && 'serviceWorker' in navigator) {
  navigator.serviceWorker
    .getRegistrations()
    .then((rs) => rs.forEach((r) => r.unregister()))
    .catch((e) => { logSwallowed(e, 'main.tsx:176'); return undefined; });
  if (typeof caches !== 'undefined') {
    caches
      .keys()
      .then((ks) => ks.forEach((k) => caches.delete(k)))
      .catch((e) => { logSwallowed(e, 'main.tsx:181'); return undefined; });
  }
}

// Register PWA service worker in production builds
// (в ОКНЕ УВЕДОМЛЕНИЙ SW не нужен — там нет приложения/PWA-установки).
if ('serviceWorker' in navigator && import.meta.env.PROD && !isNotifyWindow) {
  window.addEventListener('load', () => {
    // Принудительная проверка обновления SW при каждом старте (иначе WebView2/
    // браузер могут держать старый precache → «старый бандл» навсегда).
    // Офлайн-first не ломается: precache пересоздаётся, кэш чтения — отдельно.
    navigator.serviceWorker.register('/sw.js').then((registration) => {
      console.log('[SW] registered:', registration.scope);
      // Активная база → service worker: push чужой базы не показываем,
      // вне базы (лаунчер/выход) уведомления молчат (изоляция баз P0.1).
      const postBaseToSw = () => {
        const base = useCenterStore.getState().baseSlug || '';
        // ready — SW активирован (первый запуск: registration.active ещё null).
        navigator.serviceWorker.ready
          .then((reg) => {
            const target = navigator.serviceWorker.controller || reg.active;
            target?.postMessage({ type: 'HYDRA_SET_BASE', base });
          })
          .catch(() => { /* SW недоступен — push не показываем */ });
      };
      postBaseToSw();
      useCenterStore.subscribe(() => { postBaseToSw(); });
      void import('@/lib/eventLog').then((m) => {
        m.eventLog.info('ui', '[sw] зарегистрирован', {
          details: `scope=${registration.scope} active=${registration.active ? 'ДА' : 'нет'} waiting=${registration.waiting ? 'ДА' : 'нет'} installing=${registration.installing ? 'ДА' : 'нет'}`,
        }).catch((e) => logSwallowed(e, 'main.tsx:197'));
      });
      registration.update().catch((err) => console.warn('[SW] update check failed:', err));
      // АВТОПРИМЕНЕНИЕ ОБНОВЛЕНИЯ (PWA): тот же SSoT обнаружения, что у баннера
      // (useSwUpdateStore); свежая версия применяется сама в безопасный момент
      // (нет незавершённой отправки, не «Автономный режим») — см. lib/autoUpdate.
      void import('@/lib/autoUpdate').then((m) => m.initAutoUpdate());
      // Детерминированная проверка «сервер собрал более новый бандл» —
      // не зависит от SW-гонок (см. swUpdate.ts).
      void import('@/lib/swUpdate').then((m) => m.checkBundleVersion());
      if (registration.waiting) {
        // Старый SW ждёт — активируем новый сразу (свежий бандл при старте)
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
      // Chrome считает приложение installable только когда SW СТАЛ CONTROLLER
      // страницы (navigator.serviceWorker.controller !== null) ИЛИ уже
      // активирован. Если страница открыта БЕЗ контроллера (первый запуск/
      // свежий SW), до активации beforeinstallprompt НЕ придёт — поэтому
      // «Установить» использует isPwaInstalled()/флаг и подсказку (см.
      // pwaInstall.ts), но диагностику состояния логируем здесь.
      if (!navigator.serviceWorker.controller) {
        const t = registration.installing || registration.waiting;
        if (t) {
          t.addEventListener('statechange', () => {
            if (t.state === 'activated') {
              console.log('[SW] activated — installable');
              void import('@/lib/eventLog').then((m) => {
                m.eventLog.info('ui', '[sw] активирован (controller ready)').catch((e) => logSwallowed(e, 'main.tsx:220'));
              });
            }
          });
        }
      }
    }).catch((err) => {
      console.error('[SW] registration failed:', err);
    });

    // Новая версия фронта — проверяем и на ФОКУСЕ/online/visibilitychange
    // (не только при старте): пользователь вернулся в PWA/восстановил сеть →
    // баннер «Доступна новая версия» появится БЕЗ ручной чистки кэша.
    const check = () => { void import('@/lib/swUpdate').then((m) => m.checkBundleVersion()); };
    window.addEventListener('focus', check);
    window.addEventListener('online', check);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) check(); });
  });

  // Handle background sync messages from service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'HYDRA_SYNC') {
      baseStoreGet().then((base) => {
        if (base?.id) {
          syncEngine.syncAll(base.id).catch((err) => {
            console.warn('[SW] background sync failed:', err);
          });
        }
      });
    }
    if (event.data?.type === 'SKIP_WAITING') {
      window.location.reload();
    }
    // Клик по Web Push: открыть конкретный чат/объект в уже открытом окне
    // (без перезагрузки приложения). SW-сообщение — единый путь.
    if (event.data?.type === 'HYDRA_OPEN_URL') {
      const url = String(event.data.url || '');
      if (url) {
        void import('@/lib/navigate')
          .then((m) => m.navigateTo(url))
          .catch((e) => { logSwallowed(e, 'main.tsx:HYDRA_OPEN_URL'); return undefined; });
      }
    }
    // Новая версия фронта: SW обновился → предлагаем перезагрузку (баннер
    // «Перезагрузить для обновления»). Версия SW — в sessionStorage,
    // чтобы баннер показывался один раз за сессию.
    if (event.data?.type === 'HYDRA_APP_UPDATED') {
      const newVersion = String(event.data.version || '');
      if (newVersion && sessionStorage.getItem('hydra_sw_version') !== newVersion) {
        sessionStorage.setItem('hydra_sw_version', newVersion);
        import('@/lib/swUpdate').then((m) => m.notifyAppUpdated(newVersion));
      }
    }
  });
}

// Schedule sync on network restoration and window focus
window.addEventListener('online', () => {
  baseStoreGet().then((base) => {
    if (base?.id) syncEngine.scheduleSync(base.id);
  });
  // Синк содержимого файлов (hash-дельта) — только когда есть сеть
  void import('@/lib/fileSync').then((m) => m.syncFiles().catch((e) => logSwallowed(e, 'main.tsx:272')));
  // Офлайн-очередь загрузки файлов: чанковая докачка при восстановлении сети
  void import('@/lib/chunkedUpload').then((m) => m.flushUploadQueue().catch((e) => logSwallowed(e, 'main.tsx:274')));
  void import('@/lib/ecgOfflineQueue').then((m) => m.flushEcgStudyQueue().catch((e) => logSwallowed(e, 'main.tsx:275')));
  // Проверка новой версии бандла (баннер «Перезагрузить для обновления»)
  void import('@/lib/swUpdate').then((m) => m.checkBundleVersion());
});

window.addEventListener('focus', () => {
  baseStoreGet().then((base) => {
    if (base?.id) syncEngine.scheduleSync(base.id);
  });
  void import('@/lib/fileSync').then((m) => m.syncFiles().catch((e) => logSwallowed(e, 'main.tsx:283')));
  void import('@/lib/chunkedUpload').then((m) => m.flushUploadQueue().catch((e) => logSwallowed(e, 'main.tsx:284')));
  void import('@/lib/ecgOfflineQueue').then((m) => m.flushEcgStudyQueue().catch((e) => logSwallowed(e, 'main.tsx:285')));
  void import('@/lib/swUpdate').then((m) => m.checkBundleVersion());
});

    // Возврат на вкладку — тоже событие проверки (вкладка может жить днями;
    // registration.update() будит SW-проверку, checkBundleVersion — бандл)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        navigator.serviceWorker.getRegistration().then((r) => r?.update().catch((e) => { logSwallowed(e, 'main.tsx:292'); return undefined; })).catch((e) => { logSwallowed(e, 'main.tsx:292'); return undefined; });
        void import('@/lib/swUpdate').then((m) => m.checkBundleVersion());
      }
    });
    // pageshow: срабатывает при КАЖДОМ возврате на вкладку, включая bfcache
    // (когда visibilitychange/focus может НЕ прийти) — детерминированная проверка
    // новой версии при возврате в PWA.
    window.addEventListener('pageshow', () => {
      void import('@/lib/swUpdate').then((m) => m.checkBundleVersion());
    });

    // ПЕРИОДИЧЕСКАЯ проверка (2026-10-15, фикс «сижу с открытым PWA — тишина»):
    // событийные проверки (focus/visibilitychange/pageshow) НЕ срабатывают, если
    // пользователь НЕ покидает приложение (сидит в нём часами), а Service Worker
    // в простаивающей вкладке сам не обновляется (браузер проверяет sw.js только
    // при навигации/update()). Итог: деплой оставался незамеченным до ручного
    // обновления. Теперь: раз в минуту (вкладка видима) сверяем бандл с сервером;
    // раз в 5 минут будим проверку обновления SW — уведомление приходит в пределах
    // минуты после деплоя БЕЗ переключений окон.
    setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      void import('@/lib/swUpdate').then((m) => m.checkBundleVersion());
    }, 60_000);
    setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      navigator.serviceWorker
        .getRegistration()
        .then((r) => r?.update())
        .catch((e) => {
          logSwallowed(e, 'main.tsx:sw-update-interval');
          return undefined;
        });
    }, 5 * 60_000);

// ЕДИНЫЙ ФАЙЛ ДАННЫХ ЛАУНЧЕРА (desktop) — ДО первого рендера: подтягиваем общий
// файл (ярлыки серверных баз/центры/последний URL/кэш баз) в localStorage-зеркало
// и перечитываем centerStore. Без этого «Новое окно»/«Перезапустить» (НОВЫЙ
// профиль WebView2 → пустой localStorage) открывались бы с пустой вкладкой
// «Серверные». web/PWA — no-op (localStorage как был).
//
// ОКНО УВЕДОМЛЕНИЙ (desktop, /notify): отдельная ЛЁГКАЯ страница — приложение
// (сторы/синк/SW/шина/очереди) здесь НЕ поднимается, App не импортируется вовсе
// (его модуль запускает initApp/навигацию). Окно создаёт Go-часть
// (notify_window.go) и грузит /notify со встроенного сервера ЭТОГО экземпляра.
void (async () => {
  if (isNotifyWindow) {
    const { mountNotifyWindow } = await import('@/notify/notifyWindow');
    mountNotifyWindow();
    return;
  }
  await wailsConfigReady;
  await hydrateLauncherStorage();
  await useCenterStore.persist.rehydrate();

  const { default: App } = await import('./App');
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <I18nProvider>
        <App />
      </I18nProvider>
    </StrictMode>,
  );
  // Метка рендера (диагностика «белого экрана» — читается из кольца/CDP).
  bootLog.debug('createRoot render called');
})().catch((e: unknown) => {
  bootLog.error('инициализация клиента не прошла', { error: e instanceof Error ? e.message : String(e) });
});

// Дописать общий файл лаунчера при уходе со страницы/скрытии окна —
// свежие изменения не теряются до следующего старта окна.
window.addEventListener('pagehide', () => { void flushLauncherStorage(); });
document.addEventListener('visibilitychange', () => { if (document.hidden) void flushLauncherStorage(); });

// Контрольный снепшот через 3с: смонтировался ли React (белый экран =
// пустой #root при живом JS).
setTimeout(() => {
  bootLog.debug(
    `render snapshot: rootChildren=${document.getElementById('root')?.childElementCount ?? -1} bodyLen=${(document.body?.innerText || '').length} url=${location.pathname}`,
  );
}, 3000);
